import { useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { api } from 'src/api/root'
import { ROUTES } from 'src/types/routes'
import { STORAGE_KEYS } from '../constants/storageKeys'
import { UserRole } from 'src/types/user'
import { useTenantStore } from '../store/tenentStore'
import { useChatStore } from '../store/chatStore'
import { useShallow } from 'zustand/react/shallow'
import { logMetricsEventOnLogout } from '../api/metrics'
import { clearMarkdownCache } from './useCachedMarkdown'

const useAuth = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isAuthenticated = Boolean(
    localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
  )
  const { currentTenant, clear } = useTenantStore()
  const { clearMessages, setCreateProjectFormData, stopGeneration } = useChatStore(
    useShallow((s) => ({
      clearMessages: s.clearMessages,
      setCreateProjectFormData: s.setCreateProjectFormData,
      stopGeneration: s.stopGeneration,
    })),
  )
  const {
    data: user,
    isLoading,
    isError,
  } = api.user.currentUser.useQuery({
    enabled: isAuthenticated,
  })
  const isSuperAdmin = user?.isSuperadmin

  const userRole = (currentTenant?.tenantMemberAccess?.role as UserRole) || UserRole.VIEWER

  const logout = useCallback(
    async (returnTo?: string) => {
      logMetricsEventOnLogout()
      queryClient.clear()
      clear()
      stopGeneration()
      //Clear COPA messages:
      clearMessages()
      clearMarkdownCache()
      setCreateProjectFormData(null)
      localStorage.clear()
      sessionStorage.clear()
      navigate(ROUTES.SIGN_IN, { replace: true, state: { from: returnTo } })
    },
    [queryClient, clear, clearMessages, setCreateProjectFormData, stopGeneration, navigate],
  )

  useEffect(() => {
    if (isError) {
      logout(window.location.pathname)
    }
  }, [isError])

  useEffect(() => {
    if (user && user.tenants?.length > 0 && !currentTenant?.id) {
      useTenantStore.setState({ currentTenant: user.tenants[0] as unknown as typeof currentTenant })
    }
  }, [user, currentTenant?.id])

  useEffect(() => {
    // If all user's tenants were deleted, clear stored tenant
    if (user && currentTenant?.id && user.tenants?.length === 0) {
      useTenantStore.setState({ currentTenant: {} as typeof currentTenant })
    }
  }, [user, currentTenant?.id])

  const hasTenant = Boolean(currentTenant?.id)
  // The tenant lives in this tab's sessionStorage; the effect above picks it from the user one render later.
  const isPickingTenant = !hasTenant && Boolean(user?.tenants?.length)

  return {
    isAuthenticated,
    userRole,
    logout,
    user,
    isSuperAdmin,
    isLoading,
    hasTenant,
    isPickingTenant,
  }
}

export default useAuth
